import type { SupportedLanguage } from '../i18n';

// Tonyte's existing minimum-age eligibility rule (previously enforced via the
// 'below_16' age-group bucket in onboarding).
export const MIN_AGE = 16;

const LOCALE_BY_LANGUAGE: Record<SupportedLanguage, string> = {
  en: 'en-GB',
  de: 'de-DE',
  es: 'es-ES',
  fr: 'fr-FR',
  ru: 'ru-RU',
};

/** Parses a 'YYYY-MM-DD' string into a local-time Date, matching PickerField's convention. */
export function parseISODate(value: string | undefined | null): Date | null {
  if (!value) return null;
  const parts = value.split('-').map(Number);
  if (parts.length !== 3 || parts.some(part => Number.isNaN(part))) return null;
  const [year, month, day] = parts;
  return new Date(year, month - 1, day);
}

export function todayISODate(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Calculates age in full years against the actual current date, not just the birth year. */
export function calculateAge(dateOfBirth: string | undefined | null): number {
  const dob = parseISODate(dateOfBirth);
  if (!dob) return 0;
  const today = new Date();
  let age = today.getFullYear() - dob.getFullYear();
  const monthDiff = today.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
    age -= 1;
  }
  return Math.max(age, 0);
}

export function isDateOfBirthValid(dateOfBirth: string | undefined | null): boolean {
  const dob = parseISODate(dateOfBirth);
  if (!dob) return false;
  if (dob.getTime() > new Date().getTime()) return false;
  return calculateAge(dateOfBirth) >= MIN_AGE;
}

export function formatDateOfBirth(dateOfBirth: string | undefined | null, language: SupportedLanguage = 'en'): string {
  const dob = parseISODate(dateOfBirth);
  if (!dob) return '';
  return dob.toLocaleDateString(LOCALE_BY_LANGUAGE[language] || 'en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}
